/**
* @file ListView.js

* @author - Team E (Meeramol Mathew, Krishnapriya N, Anju Sebastian)
* @sprint - B2W-Sprint-1
* @description - 
* This component displays data in a tabular format using Ant Design's Table component.
* It includes functionalities for viewing, editing, deleting, and downloading attachments.
*/

import React from "react";
import { Space, Table, Tooltip, Popconfirm, Dropdown, Menu, Button ,notification } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, MinusOutlined } from "@ant-design/icons";
import moment from 'moment';

/**
* GridView Component
*
* Props:
* @param {Object[]} data - Array of objects representing table data.
* @param {Function} onDelete - Function to handle delete action.
* @param {Function} onView - Function to handle view action.
* @param {Function} onEdit - Function to handle edit action.
* @param {Function} onDownload - Function to handle file download.
*/

const { Column } = Table;

const GridView = ({ data, onDelete, onView, onEdit, onDownload }) => {
  console.log("Viewing:", data);

  /**
   * Handles viewing a record.
   * @param {Object} record - The record to view.
   */
  const handleView = (record) => {
    console.log("Viewing:", record);
    onView(record);
  };

  /**
   * Handles editing a record.
   * @param {Object} record - The record to edit.
   */
  const handleEdit = (record) => {
    console.log("Editing:", record);
    onEdit(record);
  };

  /**
   * Handles deleting a record.
   * @param {Object} record - The record to delete.
   */
  const handleDelete = (record) => {
    console.log("Deleting:", record);
    onDelete(record);


  };

   /**
   * Handles downloading a file.
   * @param {Object} file - The file to download.
   */
  const handleDownload = (record,file) => {
    onDownload(record,file);

  };

  return (
    <div>
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">

        {/* Table Component */}
        <Table
          dataSource={Array.isArray(data) ? data : []}  // Ensure data is an array
          rowKey="key"
          className="w-full"
          scroll={{ x: true }} // Enables horizontal scrolling if needed
          pagination={{ pageSize: 5 }}
          components={{
            header: {
              wrapper: (props) => (
                <thead {...props} className="bg-[var(--color-primary)] text-white" />
              ),
            },
          }}
          style={{ minHeight: '400px' }}  // Set minimum height
        >
          {/* Action Item Column */}
          <Column
            title="Action Item"
            dataIndex="actionItem"
            key="actionItem"
            width={550}
            align="left" // Left align header text
          />

          {/* Date Column */}
          <Column
            title="Date"
            dataIndex="date"
            key="date"
            width={200}
            align="left" // Left align header text
            render={(text) => moment(text, "DD-MM-YYYY").format("DD MMM YYYY")}
          />

           {/* Ticket Number Column */}
          <Column
            title="Ticket Number"
            dataIndex="ticketNumber"
            key="ticketNumber"
            width={200}
            align="left" // Left align header text

            render={(text) => text ? text.toUpperCase() : <MinusOutlined />}  // capitalize the ticket number values,When there is no info in ticket number show a - icon 
          />

           {/* Actions Column */}
          <Column
            title="Action"
            key="action"
            width={200}
            align="center" // Left align header text
            render={(_, record) => (
              <Space size="middle">
                 {/* View Button */}
                <Tooltip title="View">
                  <button
                    onClick={() => handleView(record)} // Call the passed function
                    className="p-2 text-blue-500 transition hover:text-blue-700"
                  >
                    <EyeOutlined className="text-lg" />
                  </button>
                </Tooltip>
                 {/* Edit Button */}
                <Tooltip title="Edit">
                  <button
                    onClick={() => handleEdit(record)}
                    className="p-2 text-green-500 transition hover:text-green-700"
                  >
                    <EditOutlined className="text-lg" />
                  </button>
                </Tooltip>
                 {/* Delete Button with Confirmation */}
                <Tooltip title="Delete">
                  <Popconfirm
                    title="Are you sure you want to delete this record?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => handleDelete(record)}                    
                    onCancel={() =>
                      notification.warning({
                        message: "Cancelled",
                        description: "Delete action was cancelled.",
                        placement: "topRight",
                        btn: (
                          <Button type="primary" size="small" onClick={() => notification.destroy()}>
                            OK
                          </Button>
                        ),
                      })
                    }
                  >
                    <button className="p-2 text-red-500 transition hover:text-red-700">
                      <DeleteOutlined className="text-lg" />
                    </button>
                  </Popconfirm>
                </Tooltip>
                {/* Download Button */}
                {record.attachment && record.attachment.length > 0 && (
                  <Dropdown
                    overlay={
                      <Menu>
                        {record.attachment.map((file, index) => {
                          // Truncate file name (show last 2 characters with "...")
                          const truncatedName =
                            file.name.length > 80
                              ? `...${file.name.slice(-80)}`
                              : file.name; // Handle short names properly

                          return (
                            <Menu.Item key={index} onClick={() => handleDownload(record,file)}>
                              <span className="flex justify-between w-full">
                                <span>{truncatedName}</span>
                                <DownloadOutlined className="ml-2" />
                              </span>
                            </Menu.Item>
                          );
                        })}
                      </Menu>
                    }
                    trigger={["click"]}
                  >
                    <Tooltip title="Download">
                      <Button type="text" icon={<DownloadOutlined />} />
                    </Tooltip>
                  </Dropdown>
                )}
              </Space>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default GridView;



